import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRangePickerProps {
  value: [Date | null, Date | null];
  onChange: (value: [Date | null, Date | null]) => void;
  placeholder?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date range'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(value[0]);
  const [endDate, setEndDate] = useState<Date | null>(value[1]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setStartDate(value[0]);
    setEndDate(value[1]);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    onChange([start, end]);
  };

  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
    }
    if (startDate) {
      return `${format(startDate, 'dd/MM/yyyy')} - ...`;
    }
    return placeholder;
  };

  const clearDates = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStartDate(null);
    setEndDate(null);
    onChange([null, null]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input Field */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full cursor-pointer"
      >
        <div className="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className={`${startDate ? 'text-gray-900' : 'text-gray-500'}`}>
              {formatDateRange()}
            </span>
          </div>
          {(startDate || endDate) && (
            <button
              onClick={clearDates}
              className="ml-2 text-gray-400 hover:text-gray-600"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 z-50 mt-1">
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            monthsShown={1}
            calendarClassName="react-datepicker-custom"
          />
        </div>
      )}

      {/* Custom Styles for react-datepicker */}
      <style>{`
        .react-datepicker-custom {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          background: white;
        }
        
        .react-datepicker-custom .react-datepicker__header {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          border-radius: 8px 8px 0 0;
        }
        
        .react-datepicker-custom .react-datepicker__current-month {
          color: #374151;
          font-weight: 600;
        }
        
        .react-datepicker-custom .react-datepicker__navigation {
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: #f3f4f6;
        }
        
        .react-datepicker-custom .react-datepicker__navigation:hover {
          background: #e5e7eb;
        }
        
        .react-datepicker-custom .react-datepicker__day--selected,
        .react-datepicker-custom .react-datepicker__day--in-selecting-range,
        .react-datepicker-custom .react-datepicker__day--in-range {
          background-color: #dbeafe;
          color: #1d4ed8;
          border-radius: 0;
          margin: 0;
        }
        
        .react-datepicker-custom .react-datepicker__day--range-start {
          background-color: #3b82f6;
          color: white;
          border-radius: 50% 0 0 50%;
          margin: 0;
        }
        
        .react-datepicker-custom .react-datepicker__day--range-end {
          background-color: #3b82f6;
          color: white;
          border-radius: 0 50% 50% 0;
          margin: 0;
        }
        
        .react-datepicker-custom .react-datepicker__day--selected.react-datepicker__day--range-start.react-datepicker__day--range-end {
          border-radius: 50%;
          margin: 0;
        }
        
        /* Remove margins from all days in selected range */
        .react-datepicker-custom .react-datepicker__week {
          display: flex;
        }
        
        .react-datepicker-custom .react-datepicker__day {
          margin: 0;
          flex: 1;
        }
        
        .react-datepicker-custom .react-datepicker__day:hover {
          background-color: #dbeafe;
          color: #1d4ed8;
        }
        
        .react-datepicker-custom .react-datepicker__day-name {
          color: #6b7280;
          font-weight: 500;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};
